import React, { useState, useEffect } from "react";
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Funnel, FunnelChart, LabelList,
    ResponsiveContainer,
    AreaChart,
    Area,
    CartesianGrid, LineChart, Line

} from "recharts";
import { ShoppingBag, RotateCcw, TrendingUp, PhoneCall, Users, Calendar, Phone } from 'lucide-react';
import { useUserContext } from "../../context/DealerProfile";

// ... all static data and color arrays as in your code ...

const COLORS = [
    "#008080", "#25B52A", "#40E0D0",  // Teal Shades
    "#FF8C00", "#FFA500", "#0884E3",  // Orange Shades
    "#4682B4", "#5F9EA0", "#87CEEB",  // Blue Shades
    "#B22222", "#DC143C", "#FF4500",  // Red Shades
    "#FF8C00", "#FFA500", "#0884E3", // Orange Shades
    "#4682B4", "#5F9EA0", "#87CEEB", // Blue Shades
    "#B22222", "#DC143C", "#FF4500", // Red Shades
    "#008080", "#25B52A", "#40E0D0", // Teal Shades
    "#800080", "#9932CC", "#BA55D3"
];


const Dashboard = ({ filter = "this-month" }) => {
    const dealerDetail = useUserContext();

    // Read ONLY from localStorage on mount
    const [totalCalls, setTotalCalls] = useState(() => localStorage.getItem("totalCalls") || 0);
    const [orderStatusData, setOrderStatusData] = useState(() => {
        const saved = localStorage.getItem("leadsBySource");
        return saved ? JSON.parse(saved) : [];
    });
    const [averageDonation, setAverageDonation] = useState(() => localStorage.getItem("averageDonation") || 0);
    const [conversionRate, setConversionRate] = useState(() => localStorage.getItem("conversionRate") || 0);
    const [uniqueContacts, setUniqueContacts] = useState(() => Number(localStorage.getItem("uniqueContactsPotential") || 0));
    const [monthlyData, setMonthlyData] = useState(() => {
        const cached = localStorage.getItem("monthlyPayments");
        return cached ? JSON.parse(cached).data || [] : [];
    });
    const [selectedYear, setSelectedYear] = useState(() => {
        const cached = localStorage.getItem("monthlyPayments");
        return cached ? JSON.parse(cached).year || new Date().getFullYear() : new Date().getFullYear();
    });
    const [followUpData, setFollowUpData] = useState(() => {
        const saved = localStorage.getItem("followUpSuccessRate");
        return saved ? JSON.parse(saved).data : null;
    });

    const [resolutionDate, setResolutionDate] = useState(
        localStorage.getItem("firstResolutionDate") || ""
    );
    const [isAllowedMonthly, setIsAllowedMonthly] = useState(true);

    const [selectedFilter, setSelectedFilter] = useState(() => localStorage.getItem("selectedFilter") || "this-month");
    const [selectedFilterLead, setSelectedFilterLead] = useState(() => localStorage.getItem("selectedFilterLead") || "today");
    const [openModal, setOpenModal] = useState(false);
    const [openModalLeads, setOpenModalLeads] = useState(false);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [customStartLeads, setCustomStartLeads] = useState("");
    const [customEndLeads, setCustomEndLeads] = useState("");
    const [loadingMain, setLoadingMain] = useState(false);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingPayments, setLoadingPayments] = useState(false);

    const [clickCountPage2, setClickCountPage2] = useState(0);
    const [isAllowedPage2, setIsAllowedPage2] = useState(true);


    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem("refreshDataPage2"));
        const now = Date.now();

        if (storedData) {
            const timePassed = now - storedData.timestamp;

            if (timePassed < 3600000) {
                // Within 1 hour → keep count
                setClickCountPage2(storedData.count);
                setIsAllowedPage2(storedData.count < 2);
            } else {
                // 1 hour passed → reset
                localStorage.setItem(
                    "refreshDataPage2",
                    JSON.stringify({ count: 0, timestamp: now })
                );
                setClickCountPage2(0);
                setIsAllowedPage2(true);
            }
        } else {
            // First time → initialize
            localStorage.setItem(
                "refreshDataPage2",
                JSON.stringify({ count: 0, timestamp: now })
            );
            setClickCountPage2(0);
            setIsAllowedPage2(true);
        }
    }, []);




    const [clickCountMonthlyPage, setClickCountMonthlyPage] = useState(0);

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem("refreshDataMonthlyPage"));
        const now = Date.now();

        if (stored) {
            const timePassed = now - stored.timestamp;

            // If within 1 hour, keep stored count and timestamp
            if (timePassed < 3600000) {
                setClickCountMonthlyPage(stored.count);
                return;
            }
        }

        // If no data OR more than 1 hour passed → reset count
        localStorage.setItem(
            "refreshDataMonthlyPage",
            JSON.stringify({ count: 0, timestamp: now })
        );
        setClickCountMonthlyPage(0);
    }, []);




    const [clickCountLeadsPage, setClickCountLeadsPage] = useState(0);
    const [isAllowedLeads, setIsAllowedLeads] = useState(true);

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem("refreshDataLeadsPage"));
        const now = Date.now();

        if (stored) {
            const timePassed = now - stored.timestamp;

            if (timePassed < 3600000) {
                setClickCountLeadsPage(stored.count);

                // 🔥 IMPORTANT: restore isAllowedLeads based on stored count
                setIsAllowedLeads(stored.count < 2);
                return;
            }
        }

        // Reset if no stored data or 1 hour passed
        localStorage.setItem(
            "refreshDataLeadsPage",
            JSON.stringify({ count: 0, timestamp: now })
        );
        setClickCountLeadsPage(0);
        setIsAllowedLeads(true); // 🔥 reset allowed
    }, []);





    // 🟢 State
    const [contactRate, setContactRate] = useState(
        localStorage.getItem("contactRatePotential") || 0
    );
    const [loadingContact, setLoadingContact] = useState(false);

    // 🟢 API Call
    const fetchContactRate = async (filter, customStart, customEnd) => {
        try {
            setLoadingContact(true);
            const res = await fetch("/server/dealer_backend/getContactRatePotential", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail.dealerDetail?.Email,
                    filter,
                    startDate: customStart,
                    endDate: customEnd,
                }),
            });

            const data = await res.json();
            if (data.status === "success") {
                setContactRate(data.data.conversionRate); // 👈 backend still returns conversionRate key
                localStorage.setItem("contactRatePotential", data.data.conversionRate);
            }
        } catch (err) {
            console.error("Error fetching contact rate:", err);
        } finally {
            setLoadingContact(false);
        }
    };

    // *** API functions ***
    const fetchCount = async (filter, startDate, endDate) => {
        setLoading(true);
        try {
            const res = await fetch("/server/dealer_backend/getCallsCount", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail?.dealerDetail?.Email,
                    filter,
                    startDate,
                    endDate,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                setTotalCalls(data.totalCalls);
                localStorage.setItem("totalCalls", data.totalCalls);
            }
        } catch (error) {
            console.error("Error fetching total calls:", error);
        } finally {
            setLoading(false);
        }
    };


    const fetchLeadsCount = async (filter, startDate, endDate) => {
        const stored = JSON.parse(localStorage.getItem("refreshDataLeadsPage"));
        const now = Date.now();

        if (stored) {
            const timePassed = now - stored.timestamp;

            // 🚫 Block if within 1 hour and limit reached
            if (timePassed < 3600000 && stored.count >= 2) {
                setIsAllowedLeads(false);
                console.log("Leads refresh limit reached for 1 hour");
                return;
            }

            // ⏳ Reset if 1 hour passed
            if (timePassed >= 3600000) {
                localStorage.setItem(
                    "refreshDataLeadsPage",
                    JSON.stringify({ count: 0, timestamp: now })
                );
                setClickCountLeadsPage(0);
                setIsAllowedLeads(true);
            }
        }

        // 🔼 Increase count
        const newCount =
            stored && now - stored.timestamp < 3600000 ? stored.count + 1 : 1;

        localStorage.setItem(
            "refreshDataLeadsPage",
            JSON.stringify({ count: newCount, timestamp: now })
        );
        setClickCountLeadsPage(newCount);
        setIsAllowedLeads(newCount < 2);

        // 🚀 Proceed with API call
        setLoadingLeads(true);
        try {
            const res = await fetch("/server/dealer_backend/getLeadsCountBySource", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail?.dealerDetail?.Email,
                    filter,
                    startDate,
                    endDate,
                }),
            });

            const data = await res.json();
            if (data.status === "success") {
                const formatted = Object.keys(data.data).map(key => ({
                    name: key,
                    value: data.data[key],
                }));
                setOrderStatusData(formatted);
                localStorage.setItem("leadsBySource", JSON.stringify(formatted));
            }
        } catch (error) {
            console.error("Error fetching leads by source:", error);
        } finally {
            setLoadingLeads(false);
        }
    };



    const fetchAverageDonation = async (filter, start, end) => {
        setLoading(true);
        try {
            const res = await fetch("/server/dealer_backend/getAverageDonation", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail?.dealerDetail?.Email,
                    filter,
                    startDate: start,
                    endDate: end,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                setAverageDonation(data.averageDonation.toFixed(2));
                localStorage.setItem("averageDonation", data.averageDonation.toFixed(2));
            }
        } catch (error) {
            console.error("Error fetching average donation:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchConversionRate = async (filter, start, end) => {
        setLoading(true);
        try {
            const res = await fetch("/server/dealer_backend/getConversionRate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail?.dealerDetail?.Email,
                    filter,
                    startDate: start,
                    endDate: end,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                setConversionRate(data.data.conversionRate);
                localStorage.setItem("conversionRate", data.data.conversionRate);
            }
        } catch (err) {
            console.error("Error fetching conversion rate:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyData = async (year) => {
        const stored = JSON.parse(localStorage.getItem("refreshDataMonthlyPage"));
        const now = Date.now();

        if (stored) {
            const timePassed = now - stored.timestamp;

            // 🚫 Block refresh if within 1 hour AND limit reached
            if (timePassed < 3600000 && stored.count >= 2) {
                console.log("Monthly refresh limit reached for this hour");
                return;
            }

            // ⏳ If more than 1 hour passed, reset count
            if (timePassed >= 3600000) {
                localStorage.setItem(
                    "refreshDataMonthlyPage",
                    JSON.stringify({ count: 0, timestamp: now })
                );
                setClickCountMonthlyPage(0);
            }
        }

        // 🚀 Increase count (either reset or increment)
        const newCount =
            stored && now - stored.timestamp < 3600000 ? stored.count + 1 : 1;

        localStorage.setItem(
            "refreshDataMonthlyPage",
            JSON.stringify({ count: newCount, timestamp: now })
        );
        setClickCountMonthlyPage(newCount);

        // 🔄 API Fetch Logic
        setLoadingPayments(true);
        try {
            const res = await fetch("/server/dealer_backend/getMonthlyPaymentSums", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail.dealerDetail?.Email,
                    year,
                }),
            });

            const data = await res.json();
            if (data.status === "success") {
                setMonthlyData(data.data);
                localStorage.setItem("monthlyPayments", JSON.stringify({ data: data.data, year }));
            }
        } catch (err) {
            console.error("Error fetching monthly payments:", err);
        } finally {
            setLoadingPayments(false);
        }
    };



    const fetchUniqueContacts = async (filter, start, end) => {
        setLoading(true);
        try {
            const res = await fetch("/server/dealer_backend/getUniqueContactsReached", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail: dealerDetail.dealerDetail?.Email,
                    filter,
                    startDate: start,
                    endDate: end,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                setUniqueContacts(data.uniqueContacts || 0);
                localStorage.setItem("uniqueContactsPotential", data.uniqueContacts || 0);
            }
        } catch (err) {
            console.error("Error fetching unique contacts", err);
        } finally {
            setLoading(false);
        }
    };
    const user_email = dealerDetail.dealerDetail?.Email;


    const fetchFollowUpData = async (dealerEmail, filter, start, end) => {
        setLoading(true);
        try {
            const res = await fetch("/server/dealer_backend/getFollowUpSuccessRate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dealerEmail,
                    filter,
                    startDate: start,
                    endDate: end,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                setFollowUpData(data.data);
                localStorage.setItem(
                    "followUpSuccessRate",
                    JSON.stringify({ data: data.data, filter, start, end })
                );
            }
        } catch (err) {
            console.error("Error fetching follow-up success rate:", err);
        } finally {
            setLoading(false);
        }
    };

    // *** Only manual actions below: ***

    const handleFilterChange = (newFilter, save = true) => {
        setSelectedFilter(newFilter);

        if (save) {
            localStorage.setItem("selectedFilter", newFilter);
        }

        if (newFilter === "custom") {
            setOpenModal(true);
            return; // No API call, so no count increase
        }

        // ⚙️ Load stored refresh data
        const stored = JSON.parse(localStorage.getItem("refreshDataPage2")) || null;
        const now = Date.now();

        if (stored) {
            const timePassed = now - stored.timestamp;

            // If less than 1 hour has passed AND limit reached ⛔
            if (timePassed < 3600000 && stored.count >= 2) {
                console.log("Limit reached! Try again after 1 hour");
                return;
            }

            // If 1 hour passed → reset count
            if (timePassed >= 3600000) {
                localStorage.setItem(
                    "refreshDataPage2",
                    JSON.stringify({ count: 0, timestamp: now })
                );
            }
        }

        // 🔢 Increase count (maintain or reset based on time)
        const newCount =
            stored && now - stored.timestamp < 3600000 ? stored.count + 1 : 1;

        localStorage.setItem(
            "refreshDataPage2",
            JSON.stringify({ count: newCount, timestamp: now })
        );
        setClickCountPage2(newCount);

        // 🚀 Proceed with API calls
        fetchCount(newFilter);
        fetchAverageDonation(newFilter);
        fetchConversionRate(newFilter);
        fetchUniqueContacts(newFilter);
        fetchFollowUpData(dealerDetail.dealerDetail?.Email, newFilter);
        fetchResolutionDate(newFilter);
        fetchContactRate(newFilter);
    };



    const handleFilterChangeLeads = (newFilter, save = true) => {
        setSelectedFilterLead(newFilter);

        if (save) {
            localStorage.setItem("selectedFilterLead", newFilter);
        }

        // 📅 If custom range is selected, open modal
        if (newFilter === "custom") {
            setOpenModalLeads(true);
            return;
        }

        // 🚫 Prevent API call if refresh limit is reached
        if (!isAllowedLeads) return;

        // 🚀 Fetch with allowed refresh limit logic
        fetchLeadsCount(newFilter, customStart, customEnd);
    };



    const handleYearChange = (e) => {
        const newYear = Number(e.target.value);
        setSelectedYear(newYear);
        fetchMonthlyData(newYear); // 👈 limit applies inside fetchMonthlyData
    };


    // Compose follow-up for display
    const followUpSuccessRate = followUpData?.successRate || 0;
    const successfulFollowUps = followUpData?.successfulFollowUps || 0;
    const totalFollowUps = followUpData?.totalFollowUps || 0;




    const fetchResolutionDate = async (filter, start, end) => {
        try {
            setLoading(true);
            const response = await fetch("/server/dealer_backend/firstResolutionDatePotential", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    // Add auth token if needed
                },
                body: JSON.stringify({
                    dealerEmail: dealerDetail?.dealerDetail?.Email,
                    filter,
                    startDate: start,
                    endDate: end,
                }),
            });

            const data = await response.json();
            if (data.status === "success") {
                // Assuming backend now returns the date as 'firstResolutionDate'
                const rate = data.uniqueContacts || "";
                setResolutionDate(rate);
                localStorage.setItem("firstResolutionDate", rate);
            }
        } catch (error) {
            console.error("Error fetching first resolution date:", error);
        } finally {
            setLoading(false);
        }
    };


    const getLastFiveYearsOptions = () => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => currentYear - i).map((yr) => (
            <option key={yr} value={yr}>
                {yr}
            </option>
        ));
    };


    return (
        <div className="p-6 mt-[-65px]">
            {/* ... Header and filters as before ... */}

            <div className="flex items-end gap-4 justify-end mb-4">
                <div className="w-[180px]">
                    <select
                        value={selectedFilter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        className="w-full py-2 px-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-[]"
                    >
                        {/* ... options ... */}
                        <option value="today">Today</option>
                        <option value="this-week">This Week</option>
                        <option value="last-week">Last Week</option>
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-year">This Year</option>
                        <option value="last-year">Last Year</option>
                        <option value="custom">Custom Range</option>
                    </select>
                </div>
                <button
                    onClick={async () => {
                        const stored = JSON.parse(localStorage.getItem("refreshDataPage2")) || null;
                        const now = Date.now();

                        if (stored) {
                            const timePassed = now - stored.timestamp;

                            // ⛔ If within 1 hour and limit reached
                            if (timePassed < 3600000 && stored.count >= 2) {
                                console.log("Limit reached! Try again after 1 hour");
                                return;
                            }

                            // ⏳ If more than 1 hour, reset the count
                            if (timePassed >= 3600000) {
                                localStorage.setItem(
                                    "refreshDataPage2",
                                    JSON.stringify({ count: 0, timestamp: now })
                                );
                                setClickCountPage2(0);
                            }
                        }

                        // 🔢 Increase count
                        const newCount =
                            stored && now - stored.timestamp < 3600000 ? stored.count + 1 : 1;

                        localStorage.setItem(
                            "refreshDataPage2",
                            JSON.stringify({ count: newCount, timestamp: now })
                        );
                        setClickCountPage2(newCount);

                        setLoadingMain(true);

                        try {
                            await Promise.all([
                                fetchCount(selectedFilter, customStart, customEnd),
                                fetchAverageDonation(selectedFilter, customStart, customEnd),
                                fetchConversionRate(selectedFilter, customStart, customEnd),
                                fetchUniqueContacts(selectedFilter, customStart, customEnd),
                                fetchFollowUpData(dealerDetail.dealerDetail?.Email, selectedFilter, customStart, customEnd),
                                fetchContactRate(selectedFilter, customStart, customEnd),
                                fetchResolutionDate(selectedFilter, customStart, customEnd),
                            ]);
                        } finally {
                            setLoadingMain(false);
                        }
                    }}
                    disabled={loadingMain || clickCountPage2 >= 2}
                    className="flex items-center gap-2 px-4 py-2 bg-[#2070B9] text-white rounded-lg shadow hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <RotateCcw className={`w-4 h-4 ${loadingMain ? "animate-spin" : ""}`} />
                    {clickCountPage2 >= 2 ? "Limit reached" : "Refresh"}
                </button>


            </div>

            {/* ... Card grid as before ... */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Calls */}
                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-green-50 rounded-lg">
                            <Phone className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Calls Made</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loading ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#2070B9">
                                            <animate attributeName="height" values="8;24;8" dur="1s" repeatCount="indefinite" begin="0s" />
                                            <animate attributeName="y" values="18;4;18" dur="1s" repeatCount="indefinite" begin="0s" />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#2070B9">
                                            <animate attributeName="height" values="18;8;18" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                            <animate attributeName="y" values="10;18;10" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#2070B9">
                                            <animate attributeName="height" values="21;12;21" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                            <animate attributeName="y" values="7;16;7" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#2070B9">
                                            <animate attributeName="height" values="24;8;24" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                            <animate attributeName="y" values="4;20;4" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#2070B9">
                                            <animate attributeName="height" values="28;12;28" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                            <animate attributeName="y" values="0;16;0" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                        </rect>
                                    </svg>

                                ) : (
                                    totalCalls
                                )}
                            </p>
                        </div>
                    </div>
                </div>



                {/* Average Donation */}
                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-purple-50 rounded-lg">
                            <ShoppingBag className="w-6 h-6 text-purple-500" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">Average Donation</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loading ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#7E57C2">
                                            <animate
                                                attributeName="height"
                                                values="8;24;8"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="18;4;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#7E57C2">
                                            <animate
                                                attributeName="height"
                                                values="18;8;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="10;18;10"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#7E57C2">
                                            <animate
                                                attributeName="height"
                                                values="21;12;21"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="7;16;7"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#7E57C2">
                                            <animate
                                                attributeName="height"
                                                values="24;8;24"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="4;20;4"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#7E57C2">
                                            <animate
                                                attributeName="height"
                                                values="28;12;28"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="0;16;0"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                        </rect>
                                    </svg>
                                ) : (
                                    <>₹ {averageDonation}</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>


                {/* Daily Revenue */}
                {/* Revenue */}
                {/* Revenue */}


                {/* Conversion Rate */}
                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-blue-500" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">Conversion Rate</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loading ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#2070BC">
                                            <animate
                                                attributeName="height"
                                                values="8;24;8"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="18;4;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#2070BC">
                                            <animate
                                                attributeName="height"
                                                values="18;8;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="10;18;10"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#2070BC">
                                            <animate
                                                attributeName="height"
                                                values="21;12;21"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="7;16;7"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#2070BC">
                                            <animate
                                                attributeName="height"
                                                values="24;8;24"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="4;20;4"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#2070BC">
                                            <animate
                                                attributeName="height"
                                                values="28;12;28"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="0;16;0"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                        </rect>
                                    </svg>
                                ) : (
                                    <>{conversionRate}%</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>




                {/* Contact Rate */}
                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-yellow-50 rounded-lg">
                            <PhoneCall className="w-6 h-6 text-yellow-500" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">Contact Rate</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loadingContact ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#FBBF24">
                                            <animate
                                                attributeName="height"
                                                values="8;24;8"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="18;4;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#FBBF24">
                                            <animate
                                                attributeName="height"
                                                values="18;8;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="10;18;10"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#FBBF24">
                                            <animate
                                                attributeName="height"
                                                values="21;12;21"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="7;16;7"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#FBBF24">
                                            <animate
                                                attributeName="height"
                                                values="24;8;24"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="4;20;4"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#FBBF24">
                                            <animate
                                                attributeName="height"
                                                values="28;12;28"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="0;16;0"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                        </rect>
                                    </svg>
                                ) : (
                                    <>{contactRate}%</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-[#E6F0FA] rounded-lg">
                            <Calendar className="w-6 h-6 text-[#2070B9]" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">First Call Resolution Rate</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loading ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#2070B9">
                                            <animate
                                                attributeName="height"
                                                values="8;24;8"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="18;4;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0s"
                                            />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#2070B9">
                                            <animate
                                                attributeName="height"
                                                values="18;8;18"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="10;18;10"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.1s"
                                            />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#2070B9">
                                            <animate
                                                attributeName="height"
                                                values="21;12;21"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="7;16;7"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.2s"
                                            />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#2070B9">
                                            <animate
                                                attributeName="height"
                                                values="24;8;24"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="4;20;4"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.3s"
                                            />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#2070B9">
                                            <animate
                                                attributeName="height"
                                                values="28;12;28"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                            <animate
                                                attributeName="y"
                                                values="0;16;0"
                                                dur="1s"
                                                repeatCount="indefinite"
                                                begin="0.4s"
                                            />
                                        </rect>
                                    </svg>
                                ) : (
                                    <>{resolutionDate || 0}</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>


                {/* Follow-Up Success Rate */}



                {/* Unique Contacts */}
                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-pink-50 rounded-lg">
                            <Users className="w-6 h-6 text-pink-500" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">Unique Contacts Reached</p>
                            <p className="text-2xl font-bold flex items-center">
                                {loading ? (
                                    <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                        <rect x="0" y="14" width="4" height="14" rx="2" fill="#DB2777">
                                            <animate attributeName="height" values="8;24;8" dur="1s" repeatCount="indefinite" begin="0s" />
                                            <animate attributeName="y" values="18;4;18" dur="1s" repeatCount="indefinite" begin="0s" />
                                        </rect>
                                        <rect x="8" y="10" width="4" height="18" rx="2" fill="#DB2777">
                                            <animate attributeName="height" values="18;8;18" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                            <animate attributeName="y" values="10;18;10" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                        </rect>
                                        <rect x="16" y="7" width="4" height="21" rx="2" fill="#DB2777">
                                            <animate attributeName="height" values="21;12;21" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                            <animate attributeName="y" values="7;16;7" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                        </rect>
                                        <rect x="24" y="4" width="4" height="24" rx="2" fill="#DB2777">
                                            <animate attributeName="height" values="24;8;24" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                            <animate attributeName="y" values="4;20;4" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                        </rect>
                                        <rect x="32" y="0" width="4" height="28" rx="2" fill="#DB2777">
                                            <animate attributeName="height" values="28;12;28" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                            <animate attributeName="y" values="0;16;0" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                        </rect>
                                    </svg>
                                ) : (
                                    <>{uniqueContacts || 0}</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>


                <div className="p-6 border rounded-lg shadow-sm bg-white">
                    <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className="p-2 bg-green-50 rounded-lg">
                            <PhoneCall className="w-6 h-6 text-green-500" />
                        </div>

                        {/* Text + Value */}
                        <div>
                            <p className="text-sm text-gray-500">Follow-Up Success Rate</p>
                            <div className="flex items-baseline gap-2">
                                <p className="text-2xl font-bold flex items-center">
                                    {loading ? (
                                        <svg width="40" height="32" viewBox="0 0 40 32" fill="none">
                                            <rect x="0" y="14" width="4" height="14" rx="2" fill="#22C55E">
                                                <animate attributeName="height" values="8;24;8" dur="1s" repeatCount="indefinite" begin="0s" />
                                                <animate attributeName="y" values="18;4;18" dur="1s" repeatCount="indefinite" begin="0s" />
                                            </rect>
                                            <rect x="8" y="10" width="4" height="18" rx="2" fill="#22C55E">
                                                <animate attributeName="height" values="18;8;18" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                                <animate attributeName="y" values="10;18;10" dur="1s" repeatCount="indefinite" begin="0.1s" />
                                            </rect>
                                            <rect x="16" y="7" width="4" height="21" rx="2" fill="#22C55E">
                                                <animate attributeName="height" values="21;12;21" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                                <animate attributeName="y" values="7;16;7" dur="1s" repeatCount="indefinite" begin="0.2s" />
                                            </rect>
                                            <rect x="24" y="4" width="4" height="24" rx="2" fill="#22C55E">
                                                <animate attributeName="height" values="24;8;24" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                                <animate attributeName="y" values="4;20;4" dur="1s" repeatCount="indefinite" begin="0.3s" />
                                            </rect>
                                            <rect x="32" y="0" width="4" height="28" rx="2" fill="#22C55E">
                                                <animate attributeName="height" values="28;12;28" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                                <animate attributeName="y" values="0;16;0" dur="1s" repeatCount="indefinite" begin="0.4s" />
                                            </rect>
                                        </svg>
                                    ) : (
                                        <>{followUpSuccessRate || 0}%</>
                                    )}
                                </p>
                                {/* {!loading && (
                                    <p className="text-xs text-gray-400">
                                        {successfulFollowUps}/{totalFollowUps} successful
                                    </p>
                                )} */}
                            </div>
                        </div>
                    </div>
                </div>




            </div>



            <div className="bg-white shadow rounded-lg p-6 mt-4">
                <div className="flex justify-between items-center mb-4">
                    <h6 className="font-bold">Month Wise Payments</h6>
                    <div className="flex gap-4 items-center">
                        <select
                            className="form-select block w-auto px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={selectedYear}
                            onChange={handleYearChange}
                        >
                            {getLastFiveYearsOptions()}
                        </select>
                        <button
                            onClick={() => fetchMonthlyData(selectedYear)}
                            disabled={loadingPayments || clickCountMonthlyPage >= 2}
                            className="flex items-center gap-1 px-3 py-2 bg-[#2070B9] text-white rounded-lg shadow hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RotateCcw className={`w-4 h-4 ${loadingPayments ? "animate-spin" : ""}`} />
                            {clickCountMonthlyPage >= 2 ? "Limit reached" : "Refresh"}
                        </button>

                    </div>
                </div>
                {/* ... Chart ... */}
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`₹${value}`, "Amount"]} />
                        <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>

            </div>

            {/* Potential Donor by Source */}
            <div className="bg-white shadow rounded-lg p-6 mt-4">
                <div className="flex justify-between items-center mb-4">
                    {/* ... */}
                    <h6 className="font-semibold">Potential Donor by Source</h6>

                    <div className="flex items-center gap-4">
                        <div className="w-[160px]">
                            <select
                                value={selectedFilterLead}
                                onChange={(e) => handleFilterChangeLeads(e.target.value)}
                                className="w-full py-1.5 px-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500 text-sm"
                            >
                                {/* ... options ... */}
                                <option value="today">Today</option>
                                <option value="this-week">This Week</option>
                                <option value="last-week">Last Week</option>
                                <option value="this-month">This Month</option>
                                <option value="last-month">Last Month</option>
                                <option value="this-year">This Year</option>
                                <option value="last-year">Last Year</option>
                                <option value="custom">Custom Range</option>

                            </select>
                        </div>
                        <button
                            onClick={() => fetchLeadsCount(selectedFilterLead, customStart, customEnd)}
                            disabled={loadingLeads || !isAllowedLeads}
                            className="flex items-center gap-2 px-3 py-1.5 bg-[#2070B9] text-white rounded-lg shadow hover:bg-blue-600 transition disabled:opacity-50 text-sm"
                        >
                            <RotateCcw className={`w-4 h-4 ${loadingLeads ? "animate-spin" : ""}`} />
                            {!isAllowedLeads ? "Limit reached, try after 1 hour" : "Refresh"}
                        </button>


                    </div>
                </div>
                {/* Pie chart or “no records” ... */}
                {orderStatusData &&
                    orderStatusData.length > 0 &&
                    orderStatusData.some(item => item.value > 0) ? (
                    <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                            <Pie
                                data={orderStatusData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={100}
                                innerRadius={60}
                                dataKey="value"
                            >
                                {orderStatusData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex flex-col items-center justify-center h-[350px] text-center">
                        {/* Big emoji or icon */}
                        <span className="text-6xl mb-4">📭</span>

                        {/* Message */}
                        <p className="text-lg font-semibold text-gray-700">No donors found</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Looks like there’s nothing to show here right now.
                        </p>

                        {/* Optional action button */}
                        <button
                            onClick={() => fetchLeadsCount(selectedFilterLead, customStart, customEnd)} // optional refresh logic
                            className="mt-4 px-4 py-2 bg-[#2070B9] text-white rounded-lg shadow hover:bg-[#185a95]"
                        >
                            Refresh
                        </button>
                    </div>


                )}

            </div>

            {/* ... Modal and the rest ... */}
            {openModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fadeIn">

                        {/* Title */}
                        <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">
                            Select Custom Date Range
                        </h2>

                        {/* Date Inputs */}
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col">
                                <label htmlFor="fromDate" className="text-sm font-medium text-gray-600 mb-1">
                                    From Date
                                </label>
                                <input
                                    id="fromDate"
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label htmlFor="toDate" className="text-sm font-medium text-gray-600 mb-1">
                                    To Date
                                </label>
                                <input
                                    id="toDate"
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={() => setOpenModal(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    fetchCount("custom", customStart, customEnd);
                                    // fetchLeadsCount("custom", customStart, customEnd);
                                    fetchAverageDonation("custom", customStart, customEnd);
                                    fetchConversionRate("custom", customStart, customEnd);
                                    fetchUniqueContacts("custom", customStart, customEnd);
                                    fetchFollowUpData(user_email, "custom", customStart, customEnd);
                                    fetchContactRate("custom", customStart, customEnd);
                                    fetchResolutionDate("custom", customStart, customEnd);

                                    setOpenModal(false);
                                }}
                                disabled={!customStart || !customEnd}
                                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {openModalLeads && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fadeIn">

                        {/* Title */}
                        <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">
                            Select Custom Date Range
                        </h2>

                        {/* Date Inputs */}
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col">
                                <label htmlFor="fromDate" className="text-sm font-medium text-gray-600 mb-1">
                                    From Date
                                </label>
                                <input
                                    id="fromDate"
                                    type="date"
                                    value={customStartLeads}
                                    onChange={(e) => setCustomStartLeads(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label htmlFor="toDate" className="text-sm font-medium text-gray-600 mb-1">
                                    To Date
                                </label>
                                <input
                                    id="toDate"
                                    type="date"
                                    value={customEndLeads}
                                    onChange={(e) => setCustomEndLeads(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={() => setOpenModalLeads(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {

                                    fetchLeadsCount("custom", customStartLeads, customEndLeads);


                                    setOpenModalLeads(false);
                                }}
                                disabled={!customStartLeads || !customEndLeads}
                                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}



        </div>
    );
};

export default Dashboard;
